import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  Feature,
  PurchaseOrderSettings,
  SellerAccountClient,
} from 'src/WebApiController';

import { useUserHasFeature } from '../useUserHasFeature';

export const usePurchaseOrderSettings = () => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  const hasAutoGeneratePONumbersFeature = useUserHasFeature(
    Feature.AutoGeneratePONumbers
  );

  const queryClient = useQueryClient();
  const queryKey = [
    'SellerAccountClient.getPurchaseOrderSettings',
    activeAccountWebClientConfig.activeAccountId,
  ];

  const shouldQuery =
    activeAccountWebClientConfig.activeAccountId != null &&
    hasAutoGeneratePONumbersFeature;

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }
      return new SellerAccountClient(
        activeAccountWebClientConfig
      ).getPurchaseOrderSettings();
    },
    enabled: shouldQuery,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation({
    mutationFn: async (settings: PurchaseOrderSettings) => {
      return new SellerAccountClient(
        activeAccountWebClientConfig
      ).mergePurchaseOrderSettings(settings);
    },
    onError: (err: ErrorTypes, settings) => {
      showErrorDialog(
        'SellerAccountClient.mergePurchaseOrderSettings',
        err,
        { trackErrorData: { settings } }
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetched: query.isFetched,
    mutation,
  };
};
